<template>
  <div class="bg-white rounded-xl border border-gray-200/60 mb-6 card-shadow">
    <div class="flex items-end gap-3 px-4 pt-3 pb-2 flex-wrap">
      <!-- Date range — visible on every tab. The picker itself is
           identical across tabs; only what's around it changes. -->
      <div class="shrink-0">
        <div class="w-[330px]">
          <VueDatePicker
            v-model="dateRangeValue"
            range
            :multi-calendars="true"
            :enable-time-picker="true"
            :time-picker-inline="true"
            :preset-dates="presetDates"
            :max-date="maxDate"
            :auto-apply="false"
            format="yyyy-MM-dd HH:mm"
            placeholder="Select date range"
            input-class-name="custom-dp-input"
            :menu-class-name="menuClassName"
            :clearable="false"
            position="left"
            teleport="body"
            @update:model-value="onDateRangeChange"
          >
            <template #input-icon>
              <svg class="w-4 h-4 text-gray-400 ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </template>
            <template #lmp-preset="{ label, value, presetDate }">
              <div
                :class="['lmp-preset-item', { 'lmp-preset-active': isPresetActive(label) }]"
                @click="onPresetClick(label, value, presetDate)"
              >
                <span class="lmp-preset-dot"></span>
                <span class="lmp-preset-label">{{ label }}</span>
              </div>
            </template>
            <template #action-row="{ internalModelValue, selectDate, closePicker }">
              <div class="lmp-action-row">
                <div class="lmp-action-info">
                  <svg class="w-3.5 h-3.5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  <span>Max allowed: <strong>{{ maxDateLabel }}</strong></span>
                </div>
                <div class="lmp-action-buttons">
                  <button class="lmp-btn-cancel" type="button" @click="closePicker">Cancel</button>
                  <button
                    class="lmp-btn-apply"
                    type="button"
                    :disabled="!isRangeValid(internalModelValue)"
                    @click="selectDate(internalModelValue)"
                  >Apply</button>
                </div>
              </div>
            </template>
          </VueDatePicker>
        </div>
      </div>

      <!-- App picker — shown on every tab this toolbar handles
           (overview / lives / medias). The Ads tab uses AdsToolbar,
           not this component, so no need to gate it further. Sits
           right next to the date picker so the most-used cross-
           section (which app emitted the events) is visible without
           scanning further right. -->
      <div class="shrink-0">
        <div class="w-[200px]">
          <SearchSelect
            ref="appSelect"
            v-model="selectedAppName"
            :options="appNameOptions"
            placeholder="All apps"
            search-placeholder="Search apps..."
            label-key="name"
            value-key="id"
            :icon="appIcon"
            :remote="true"
            :compact="true"
            @update:model-value="onAppNamePicked"
            @search="onAppNameSearch"
          />
        </div>
      </div>

      <!-- Category — every tab this toolbar handles. The API expands
           each category to its lives / medias (subcategories
           included), so all KPIs and charts on the tab are scoped. -->
      <div class="shrink-0">
        <div class="w-[240px]">
          <CategorySelect
            ref="categorySelect"
            v-model="selectedCategoryIds"
            placeholder="All categories"
            compact
            @update:model-value="onCategoriesPicked"
          />
        </div>
      </div>

      <!-- Live + Show — only on the Lives tab. -->
      <template v-if="tab === 'lives'">
        <div class="shrink-0">
          <div class="w-[240px]">
            <SearchSelect
              ref="liveSelect"
              v-model="selectedLive"
              :options="lives"
              placeholder="All lives"
              search-placeholder="Search lives..."
              label-key="name"
              value-key="_id"
              :icon="liveIcon"
              :remote="true"
              :compact="true"
              @update:model-value="onLivePicked"
              @search="onLiveSearch"
            />
          </div>
        </div>
        <div v-if="selectedLive" class="shrink-0">
          <div class="w-[200px]">
            <SearchSelect
              ref="showSelect"
              v-model="selectedShow"
              :options="showOptions"
              placeholder="All shows"
              search-placeholder="Search shows..."
              label-key="title"
              value-key="id"
              :icon="showIcon"
              :remote="true"
              :compact="true"
              @update:model-value="onShowPicked"
              @search="onShowSearch"
            />
          </div>
        </div>
      </template>

      <!-- Media — only on the Medias tab. -->
      <template v-if="tab === 'medias'">
        <div class="shrink-0">
          <div class="w-[240px]">
            <SearchSelect
              ref="mediaSelect"
              v-model="selectedMedia"
              :options="medias"
              placeholder="All medias"
              search-placeholder="Search medias..."
              label-key="name"
              value-key="_id"
              :icon="mediaIcon"
              :remote="true"
              :compact="true"
              @update:model-value="onMediaPicked"
              @search="onMediaSearch"
            />
          </div>
        </div>
      </template>

      <div class="flex-1"></div>

      <!-- Download — PDF / CSV of what the tab shows for the APPLIED
           filters. The parent shell asks the active tab for its data. -->
      <div class="shrink-0 relative" ref="downloadMenu">
        <button
          type="button"
          @click="downloadOpen = !downloadOpen"
          :disabled="loading"
          :class="[
            'h-[36px] px-4 rounded-lg border text-[13px] font-medium transition-all duration-150 inline-flex items-center gap-2',
            loading ? 'border-gray-200 text-gray-300 cursor-not-allowed' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:text-gray-900 cursor-pointer'
          ]"
          :title="dirty ? 'Downloads the results currently shown. Press Search first to apply your new filters.' : 'Download this report'"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          <span>Download</span>
        </button>
        <div v-if="downloadOpen" class="absolute right-0 top-full mt-1 z-50 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
          <button type="button" @click="onDownload('pdf')" class="w-full text-left px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50">PDF report</button>
          <button type="button" @click="onDownload('csv')" class="w-full text-left px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50">CSV (Excel)</button>
        </div>
      </div>

      <!-- Search button — applies pending → applied. -->
      <div class="shrink-0">
        <button
          @click="onSearch"
          :disabled="loading || !canSearch"
          :class="[
            'h-[36px] px-5 rounded-lg text-[13px] font-medium transition-all duration-150 inline-flex items-center gap-2',
            canSearch && !loading
              ? 'bg-[var(--color-primary)] text-white hover:opacity-90 cursor-pointer shadow-sm'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          ]"
        >
          <svg v-if="loading" class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <circle cx="12" cy="12" r="10" stroke-opacity="0.25"/>
            <path d="M22 12a10 10 0 01-10 10" stroke-linecap="round"/>
          </svg>
          <svg v-else class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <span>{{ loading ? 'Loading…' : 'Search' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import SearchSelect from '../../../components/SearchSelect.vue'
import CategorySelect from '../../../components/CategorySelect.vue'
import api from '../../../services/api.js'

/**
 * Shared toolbar for the analytics section. Renders inside the parent
 * shell ABOVE the tab body so the same instance survives tab
 * switches — preserves the date range when going Overview ↔ Lives ↔
 * Medias without forcing the user to re-pick.
 *
 * State model:
 *   - The DATE PICKER is locally controlled (its own v-model), then
 *     pushed to `pending.dateFrom/dateTo` via `setPending`. We don't
 *     bind the picker directly to the store because the picker emits
 *     on every internal tweak and we want a single staged commit.
 *   - The SELECTORS (live / show / media / format) write straight
 *     through to `pending.*` on each pick — they're discrete enough
 *     that the staged-vs-applied distinction is pickup-by-the-Search
 *     button anyway.
 *   - `loading` is forwarded by the active child (overview / lives /
 *     medias) via the `loading` prop so the Search button shows a
 *     spinner while THAT view is fetching.
 *
 * The toolbar conditionally shows live/show or media/format selectors
 * based on the `tab` prop the parent passes from `$route.name`.
 */
export default {
  name: 'AnalyticsToolbar',
  components: { VueDatePicker, SearchSelect, CategorySelect },

  props: {
    // 'overview' | 'lives' | 'medias' — controls which selectors to show.
    tab: { type: String, required: true },
    // True while the active child view is fetching analytics data.
    loading: { type: Boolean, default: false }
  },

  data() {
    return {
      // Picker model — owned locally, mirrored into store on commit.
      dateRangeValue: null,
      activePresetLabel: 'Today',
      now: new Date(),
      _nowTimer: null,

      // Selector models — mirrored into store on each pick.
      selectedLive:    '',
      selectedShow:    '',
      selectedMedia:   '',
      selectedAppName: '',
      selectedCategoryIds: [],
      downloadOpen: false,

      lives: [],
      shows: [],
      medias: [],
      apps:  [],

      liveIcon:  '<svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>',
      showIcon:  '<svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>',
      mediaIcon: '<svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/></svg>',
      appIcon:   '<svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>'
    }
  },

  computed: {
    ...mapGetters('analytics', ['pending', 'dirty', 'applied']),

    /**
     * Upper bound for both date pickers: now − 1 minute. Recomputes
     * whenever `now` ticks (every 30s) so the cap stays current.
     */
    maxDate() {
      return new Date(this.now.getTime() - 60 * 1000)
    },
    maxDateLabel() {
      return this.maxDate.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit'
      })
    },

    /**
     * Preset shortcuts shown in the picker's left sidebar. Same nine
     * presets as the old monolithic page plus a 'Custom' entry that
     * unlocks the calendar grids.
     */
    presetDates() {
      const top = this.maxDate
      const hour = 60 * 60 * 1000
      const day  = 24 * hour
      const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x }
      const endOfDay   = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x }
      const yesterday  = new Date(top.getTime() - day)
      const startOfThisMonth = new Date(top.getFullYear(), top.getMonth(), 1)
      const startOfLastMonth = new Date(top.getFullYear(), top.getMonth() - 1, 1)
      const endOfLastMonth   = new Date(top.getFullYear(), top.getMonth(), 0, 23, 59, 59, 999)
      const startOfLastYear  = new Date(top.getFullYear() - 1, 0, 1)
      const endOfLastYear    = new Date(top.getFullYear() - 1, 11, 31, 23, 59, 59, 999)
      const slot = 'lmp-preset'
      return [
        { slot, label: 'Last Hour',     value: [new Date(top.getTime() - hour),     top] },
        { slot, label: 'Last 6 Hours',  value: [new Date(top.getTime() - 6 * hour), top] },
        // "Today" runs from local midnight up to the current hour
        // boundary (top is the current time clamped to the top of
        // the hour by the toolbar). At 00:xx it'll be a tiny window
        // — that's expected; the user is asking about today.
        { slot, label: 'Today',         value: [startOfDay(top),                    top] },
        { slot, label: 'Last 24 Hours', value: [new Date(top.getTime() - day),      top] },
        { slot, label: 'Yesterday',     value: [startOfDay(yesterday), endOfDay(yesterday)] },
        { slot, label: 'Last 7 Days',   value: [new Date(top.getTime() - 7 * day),  top] },
        { slot, label: 'Last 30 Days',  value: [new Date(top.getTime() - 30 * day), top] },
        { slot, label: 'This Month',    value: [startOfThisMonth, top] },
        { slot, label: 'Last Month',    value: [startOfLastMonth, endOfLastMonth] },
        { slot, label: 'Last Year',     value: [startOfLastYear,  endOfLastYear] },
        { slot, label: 'Custom',        value: this.dateRangeValue }
      ]
    },

    menuClassName() {
      const lock = this.activePresetLabel !== 'Custom' ? 'lmp-mode-locked' : ''
      return `custom-dp-menu lmp-range-menu ${lock}`.trim()
    },

    /**
     * Search button enabled whenever the staged date range is valid
     * (both ends present, neither past the max-date cap). Pending
     * changes (live / show / media picks) don't gate it — the user
     * can always re-run the current filters as a refresh.
     */
    canSearch() {
      const r = this.dateRangeValue
      if (!Array.isArray(r) || r.length !== 2 || !r[0] || !r[1]) return false
      const max = this.maxDate
      return new Date(r[0]) <= max && new Date(r[1]) <= max
    },

    showOptions() {
      return [{ id: '', title: 'All shows' }, ...this.shows]
    },

    /**
     * App-name dropdown options — dynamic. Sourced from the
     * analytics_dimensions collection (loaded via loadApps) so any
     * `app_name` value the SDK actually emitted appears in the filter
     * without requiring a client redeploy. Empty-id row prepended so
     * the user can clear the filter the same way as the other pickers.
     * Token pass straight through as `?appName=...` — the reader
     * accepts any value (allowlist dropped 2026-07-16), so an unknown
     * pick just yields empty results.
     */
    appNameOptions() {
      return [{ id: '', name: 'All apps' }, ...this.apps]
    }
  },

  watch: {
    // Keep local pickers in sync if the store is reseeded externally
    // (deep-link, programmatic navigation).
    'pending.liveId'(v)  { if (v !== this.selectedLive)    this.selectedLive    = v || '' },
    'pending.showId'(v)  { if (v !== this.selectedShow)    this.selectedShow    = v || '' },
    'pending.mediaId'(v) { if (v !== this.selectedMedia)   this.selectedMedia   = v || '' },
    'pending.appName'(v) { if (v !== this.selectedAppName) this.selectedAppName = v || '' },
    'pending.categoryIds'(v) {
      if (JSON.stringify(v || []) !== JSON.stringify(this.selectedCategoryIds)) this.selectedCategoryIds = [...(v || [])]
    },

    /**
     * Tab change — load the entity list the new tab needs. The toolbar
     * is rendered once in the parent shell and persists across tab
     * switches, so `mounted()` only fires on the very first landing.
     * This watcher fills the gap when the user navigates to a tab whose
     * list hasn't been seeded yet (Lives → Medias and back).
     */
    tab: {
      immediate: true,
      handler(v) {
        // App picker is visible on overview / lives / medias, so
        // seed the app list on any of those tabs when it hasn't been
        // fetched yet.
        if (!this.apps.length) this.loadApps()
        if (v === 'lives'  && !this.lives.length)  this.loadLives()
        if (v === 'medias' && !this.medias.length) this.loadMedias()
        if (v === 'lives'  && this.selectedLive && !this.shows.length) this.loadShows()
      }
    },
    'pending.dateFrom'(v, prev) {
      // Reseed the picker only if both ends changed externally.
      if (v && this.pending.dateTo && (v !== prev)) {
        this.dateRangeValue = [new Date(this.pending.dateFrom), new Date(this.pending.dateTo)]
      }
    }
  },

  mounted() {
    // Seed picker from store. Default presetLabel from store keeps
    // the active-state highlight in sync with whatever was last
    // applied. Clamp the upper end against `maxDate` (now − 1 min)
    // because the store seeds `dateTo: new Date()` which is always
    // marginally past the cap by the time `mounted` fires — without
    // this clamp `canSearch` is false on first paint and the Search
    // button renders disabled until the user picks a new range.
    const max = this.maxDate
    let from = this.pending.dateFrom ? new Date(this.pending.dateFrom) : null
    let to   = this.pending.dateTo   ? new Date(this.pending.dateTo)   : null
    if (to && to > max) to = max
    if (from && from > max) from = max
    this.dateRangeValue = [from, to]
    this.activePresetLabel = this.pending.presetLabel || 'Custom'
    this.selectedLive    = this.pending.liveId  || ''
    this.selectedShow    = this.pending.showId  || ''
    this.selectedMedia   = this.pending.mediaId || ''
    this.selectedAppName = this.pending.appName || ''
    this.selectedCategoryIds = [...(this.pending.categoryIds || [])]

    // Deep-link seed: if the URL landed on /analytics/lives/<id>, the
    // store seeds pending.liveId before the toolbar mounts. The `tab`
    // watcher with `immediate: true` runs BEFORE this block, when
    // `selectedLive` is still '' from data(), so its loadShows check
    // misses. Trigger it explicitly here once selectedLive is set.
    if (this.tab === 'lives' && this.selectedLive && !this.shows.length) {
      this.loadShows()
    }

    // Tick `now` every 30s so maxDate / maxDateLabel stay fresh.
    // Initial entity-list load is handled by the `tab` watcher with
    // `immediate: true` — it covers the first landing and every later
    // tab switch with one path.
    this._nowTimer = setInterval(() => { this.now = new Date() }, 30 * 1000)

    this._onDocClick = (e) => {
      if (this.downloadOpen && !this.$refs.downloadMenu?.contains(e.target)) this.downloadOpen = false
    }
    document.addEventListener('click', this._onDocClick)
  },

  beforeUnmount() {
    if (this._nowTimer) { clearInterval(this._nowTimer); this._nowTimer = null }
    document.removeEventListener('click', this._onDocClick)
  },

  methods: {
    // ---- Date picker ----
    onPresetClick(label, value, presetDate) {
      this.activePresetLabel = label
      if (label === 'Custom') return
      presetDate(value)
    },

    onDateRangeChange(value) {
      if (Array.isArray(value) && value.length === 2 && value[0] && value[1]) {
        const max = this.maxDate
        let [from, to] = value.map(d => new Date(d))
        let clamped = false
        if (to > max)   { to = new Date(max);   clamped = true }
        if (from > max) { from = new Date(max); clamped = true }
        if (clamped) this.dateRangeValue = [from, to]
        this.$store.dispatch('analytics/setPending', {
          dateFrom: from,
          dateTo:   to,
          presetLabel: this.activePresetLabel
        })
      }
    },

    isPresetActive(label) {
      return this.activePresetLabel === label
    },

    isRangeValid(value) {
      if (!Array.isArray(value) || value.length !== 2) return false
      const [from, to] = value
      if (!from || !to) return false
      const max = this.maxDate
      return new Date(from) <= max && new Date(to) <= max && new Date(from) <= new Date(to)
    },

    // ---- Lives tab selectors ----
    // All entity loaders read from /analytics/dimensions — one endpoint,
    // per-type params. Sorted by last_seen_at desc, capped at 50 rows,
    // substring search on target_name. Backed by analytics_dimensions
    // (writer stamps rows on session_started) so the dropdown reflects
    // dimensions the analytics actually saw, not the source-of-truth
    // catalog which may include unused entries.
    async loadLives(search = '') {
      try {
        const params = { type: 'live', limit: 50 }
        if (search) params.search = search
        const res = await api.get('/analytics/dimensions', { params, silent: true })
        const items = Array.isArray(res.data) ? res.data : []
        this.lives = items.map(d => ({ _id: d.target_id, name: d.target_name }))
        this.$refs.liveSelect?.setLoading(false)
      } catch {
        this.$refs.liveSelect?.setLoading(false)
      }
    },

    async loadShows(search = '') {
      if (!this.selectedLive) { this.shows = []; return }
      try {
        const params = { type: 'show', parentId: this.selectedLive, limit: 50 }
        if (search) params.search = search
        const res = await api.get('/analytics/dimensions', { params, silent: true })
        const items = Array.isArray(res.data) ? res.data : []
        // showOptions expects { id, title } — normalize here.
        this.shows = items.map(d => ({ id: d.target_id, title: d.target_name }))
      } catch {
        this.shows = []
      }
    },

    onLivePicked() {
      this.selectedShow = ''
      this.shows = []
      this.$store.dispatch('analytics/setPending', {
        liveId: this.selectedLive || null,
        showId: null
      })
      if (this.selectedLive) this.loadShows()
    },

    onLiveSearch(query) { this.loadLives(query) },

    onShowPicked() {
      this.$store.dispatch('analytics/setPending', { showId: this.selectedShow || null })
    },

    onShowSearch(query) { this.loadShows(query) },

    // ---- Medias tab selectors ----
    async loadMedias(search = '') {
      try {
        const params = { type: 'media', limit: 50 }
        if (search) params.search = search
        const res = await api.get('/analytics/dimensions', { params, silent: true })
        const items = Array.isArray(res.data) ? res.data : []
        this.medias = items.map(d => ({ _id: d.target_id, name: d.target_name }))
        this.$refs.mediaSelect?.setLoading(false)
      } catch {
        this.$refs.mediaSelect?.setLoading(false)
      }
    },

    onMediaPicked() {
      this.$store.dispatch('analytics/setPending', {
        mediaId: this.selectedMedia || null
      })
    },

    onMediaSearch(query) { this.loadMedias(query) },

    // ---- App name (Lives tab) ----
    async loadApps(search = '') {
      try {
        const params = { type: 'app', limit: 50 }
        if (search) params.search = search
        const res = await api.get('/analytics/dimensions', { params, silent: true })
        const items = Array.isArray(res.data) ? res.data : []
        // App target_id === target_name === the SDK's app_name value.
        this.apps = items.map(d => ({ id: d.target_id, name: d.target_name }))
        this.$refs.appSelect?.setLoading(false)
      } catch {
        this.$refs.appSelect?.setLoading(false)
      }
    },

    onAppNamePicked() {
      this.$store.dispatch('analytics/setPending', {
        appName: this.selectedAppName || null
      })
    },

    onAppNameSearch(query) { this.loadApps(query) },

    // ---- Category (every tab) ----
    onCategoriesPicked(ids) {
      this.$store.dispatch('analytics/setPending', {
        categoryIds: (ids || []).map(id => id?.toString())
      })
    },

    // ---- Download ----
    /**
     * Human-readable summary of the APPLIED filters for the report
     * header. `subject` is the report's headline (categories, else the
     * selected live / media).
     */
    reportFilters() {
      const a = this.applied
      const fmt = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      const nameIn = (list, key, id, label) => (list.find(x => String(x[key]) === String(id)) || {})[label] || id
      const categories = (a.categoryIds || []).map(id => this.$refs.categorySelect?.getCategoryName(id) || id)

      const filters = [['Period', `${fmt(a.dateFrom)} to ${fmt(a.dateTo)} (${tz})`]]
      if (categories.length) filters.push([categories.length > 1 ? 'Categories' : 'Category', categories.join(', ')])
      let entity = ''
      if (this.tab === 'lives' && a.liveId) {
        entity = nameIn(this.lives, '_id', a.liveId, 'name')
        filters.push(['Live', entity])
        if (a.showId) filters.push(['Show', nameIn(this.shows, 'id', a.showId, 'title')])
      }
      if (this.tab === 'medias' && a.mediaId) {
        entity = nameIn(this.medias, '_id', a.mediaId, 'name')
        filters.push(['Media', entity])
      }
      if (a.appName) filters.push(['App', nameIn(this.apps, 'id', a.appName, 'name')])
      return { subject: categories.join(', ') || entity, filters }
    },

    onDownload(format) {
      this.downloadOpen = false
      this.$emit('download', { format, ...this.reportFilters() })
    },

    // ---- Search button ----
    onSearch() {
      if (!this.canSearch || this.loading) return
      this.$store.dispatch('analytics/apply')
      this.$emit('search')
    }
  }
}
</script>

<!--
  Non-scoped popover styles. The picker menu is rendered via
  `teleport="body"`, which lifts it out of this component's DOM
  subtree. Scoped CSS would silently fail to apply. Everything below
  is keyed under `.lmp-range-menu` (the menu-class) so it can't bleed
  into other VueDatePickers on the page.
-->
<style>
.dp__main:has(.custom-dp-input) {
  --dp-font-size: 13px;
  --dp-input-padding: 0 10px 0 32px;
  --dp-input-icon-padding: 32px;
  --dp-border-radius: 8px;
  line-height: 1.2 !important;
}
input.dp__input.custom-dp-input,
.custom-dp-input {
  height: 36px !important;
  min-height: 36px !important;
  max-height: 36px !important;
  width: 100% !important;
  border: 1px solid #e5e5e5 !important;
  border-radius: 8px !important;
  font-family: 'Poppins', sans-serif !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  line-height: 18px !important;
  color: #262626 !important;
  padding: 0 10px 0 32px !important;
  background: #fff !important;
  box-sizing: border-box !important;
}
input.dp__input.custom-dp-input:focus,
.custom-dp-input:focus {
  border-color: var(--color-primary) !important;
  box-shadow: 0 0 0 3px var(--color-primary-glow) !important;
  outline: none !important;
}
input.dp__input.custom-dp-input::placeholder,
.custom-dp-input::placeholder {
  color: #a3a3a3 !important;
  font-weight: 500 !important;
}

.lmp-range-menu.dp__theme_light {
  --dp-primary-color: #3393ff;
  --dp-primary-text-color: #fff;
  --dp-font-family: 'Poppins', sans-serif;
  --dp-border-radius: 12px;
  --dp-cell-border-radius: 8px;
  --dp-font-size: 13px;
  --dp-menu-min-width: 780px;
  --dp-border-color: #e5e5e5;
  --dp-menu-border-color: #e5e5e5;
  --dp-hover-color: rgba(51, 147, 255, 0.08);
  --dp-hover-text-color: #262626;
  --dp-range-between-dates-background-color: rgba(51, 147, 255, 0.08);
  --dp-range-between-dates-text-color: #262626;
}
.lmp-range-menu {
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
  overflow: hidden !important;
}
.lmp-range-menu .dp--preset-dates {
  padding: 10px 8px !important;
  border-right: 1px solid #f0f0f0 !important;
  min-width: 168px !important;
  background: #fafafa !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 2px !important;
}
.lmp-range-menu .dp__btn.dp--preset-range {
  all: unset !important;
  display: block !important;
  cursor: pointer !important;
}
.lmp-range-menu .lmp-preset-item {
  display: flex !important;
  align-items: center !important;
  gap: 10px !important;
  padding: 8px 12px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  color: #525252 !important;
  border-radius: 8px !important;
  cursor: pointer !important;
  transition: background 120ms ease, color 120ms ease !important;
}
.lmp-range-menu .lmp-preset-item:hover {
  background: rgba(51, 147, 255, 0.08) !important;
  color: #3393ff !important;
}
.lmp-range-menu .lmp-preset-dot {
  width: 6px !important;
  height: 6px !important;
  border-radius: 50% !important;
  background: #d4d4d4 !important;
  flex-shrink: 0 !important;
  transition: background 120ms ease, transform 120ms ease !important;
}
.lmp-range-menu .lmp-preset-item:hover .lmp-preset-dot {
  background: #3393ff !important;
}
.lmp-range-menu .lmp-preset-active {
  background: #3393ff !important;
  color: #fff !important;
}
.lmp-range-menu .lmp-preset-active:hover {
  background: #3393ff !important;
  color: #fff !important;
  opacity: 0.95 !important;
}
.lmp-range-menu .lmp-preset-active .lmp-preset-dot {
  background: #fff !important;
  transform: scale(1.2) !important;
}
.lmp-range-menu .lmp-preset-active:hover .lmp-preset-dot {
  background: #fff !important;
}
.lmp-range-menu .lmp-preset-label {
  flex: 1 !important;
  white-space: nowrap !important;
}

.lmp-range-menu .dp__instance_calendar:not(:has(.dp__instance_calendar)) {
  position: relative !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  padding: 30px 12px 12px !important;
}
.lmp-range-menu .dp__instance_calendar:not(:has(.dp__instance_calendar))::before {
  position: absolute;
  top: 8px;
  left: 12px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  pointer-events: none;
  content: 'From Date';
}
.lmp-range-menu .dp__instance_calendar:not(:has(.dp__instance_calendar)) ~ .dp__instance_calendar::before {
  content: 'To Date';
}
.lmp-range-menu .dp__instance_calendar:not(:has(.dp__instance_calendar)) ~ .dp__instance_calendar {
  border-left: 1px solid #f0f0f0 !important;
}
.lmp-range-menu .dp__time_input_wrap,
.lmp-range-menu .dp__inline_inputs,
.lmp-range-menu .dp__time_picker_inline_container {
  align-self: center !important;
  margin-top: 10px !important;
  padding-top: 10px !important;
  border-top: 1px solid #f3f3f3 !important;
  width: auto !important;
  display: flex !important;
  justify-content: center !important;
}

.lmp-range-menu .dp__action_row {
  width: 100% !important;
  box-sizing: border-box !important;
  padding: 0 !important;
}
.lmp-range-menu .lmp-action-row {
  width: 100% !important;
  box-sizing: border-box !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 12px !important;
  padding: 10px 14px !important;
  border-top: 1px solid #f0f0f0 !important;
  background: #fafafa !important;
  border-bottom-left-radius: 12px !important;
  border-bottom-right-radius: 12px !important;
}
.lmp-range-menu .lmp-action-info {
  display: flex !important;
  align-items: center !important;
  gap: 6px !important;
  font-size: 12px !important;
  color: #6b7280 !important;
}
.lmp-range-menu .lmp-action-info svg {
  color: #9ca3af !important;
  flex-shrink: 0 !important;
}
.lmp-range-menu .lmp-action-info strong {
  color: #262626 !important;
  font-weight: 600 !important;
}
.lmp-range-menu .lmp-action-buttons {
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
}
.lmp-range-menu .lmp-btn-cancel,
.lmp-range-menu .lmp-btn-apply {
  height: 32px !important;
  padding: 0 16px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  font-family: 'Poppins', sans-serif !important;
  border-radius: 8px !important;
  border: none !important;
  cursor: pointer !important;
  transition: background 120ms ease, opacity 120ms ease !important;
}
.lmp-range-menu .lmp-btn-cancel {
  background: #f5f5f5 !important;
  color: #525252 !important;
}
.lmp-range-menu .lmp-btn-cancel:hover {
  background: #ebebeb !important;
}
.lmp-range-menu .lmp-btn-apply {
  background: #3393ff !important;
  color: #fff !important;
}
.lmp-range-menu .lmp-btn-apply:hover {
  opacity: 0.9 !important;
}
.lmp-range-menu .lmp-btn-apply:disabled,
.lmp-range-menu .lmp-btn-apply[disabled] {
  background: #c7d8ea !important;
  color: #fff !important;
  cursor: not-allowed !important;
  opacity: 1 !important;
}

.lmp-range-menu .dp__range_start.dp__range_end,
.lmp-range-menu .dp__cell_inner.dp__range_start.dp__range_end {
  border-radius: var(--dp-cell-border-radius, 8px) !important;
}

.lmp-range-menu.lmp-mode-locked .dp__instance_calendar:not(:has(.dp__instance_calendar)),
.lmp-range-menu.lmp-mode-locked .dp__time_input_wrap,
.lmp-range-menu.lmp-mode-locked .dp__inline_inputs,
.lmp-range-menu.lmp-mode-locked .dp__time_picker_inline_container {
  opacity: 0.55 !important;
  pointer-events: none !important;
  user-select: none !important;
}
</style>
